/**
 * The portal's invoice policy: A RESELLER GENERATES NO INVOICES (2026-09-13).
 *
 * Invoices go one way — XOP Networks bills the reseller, and the reseller pays.
 * What a reseller asks XOP for is a QUOTE (`/quotes/request`); what it owes XOP
 * is an invoice XOP raised, and it appears here read-only. A reseller's own
 * customer-facing billing is its own business and is not run from this portal.
 *
 * IT LIVES HERE, OUTSIDE `src/crm`, for the reason the README's table gives
 * for every other policy: the ported tree is a copy that must stay diffable
 * against xop-edge-staff and is excluded from this repo's type program, so a
 * rule written in there would be neither typechecked nor comfortably testable.
 * The one edited ported file (`pages/invoices/InvoicesPage.tsx`) does nothing
 * but READ this constant.
 *
 * Why a constant and not a permission: `invoices:edit` is what lets a reseller
 * REQUEST a quote and decide one, so revoking it to stop invoice creation
 * would take the quote flow with it. The two are different rights that happen
 * to share a key upstream, and this is the portal drawing the line.
 */
export const RESELLER_MAY_CREATE_INVOICES = false;

/* 2026-09-14: the editor this used to gate (`pages/invoices/generate/*`,
 * `components/generate-invoice-form*`, the creation-method selector and the
 * have-invoice form) is DELETED from this tree, not merely unrouted. The
 * constant stays as the statement of the rule; nothing reads it any more
 * except its tests. `/invoices/generate` is a redirect to the list. */

/** What the Invoices section is, in one line, now that it is read-only. */
export const RESELLER_INVOICES_DESCRIPTION =
  "Invoices XOP Networks has issued to you. To ask XOP for pricing, request a quote.";

/**
 * The list's heading, replacing the source's "All Invoices". The source also
 * renders an "All Invoices ∣ Recurring Invoices" tab strip; both tabs describe
 * things a reseller does not have (it raises no invoices, so it schedules
 * none), so the strip and its component were deleted (2026-09-14).
 */
export const RESELLER_INVOICES_TITLE = "Invoices from XOP Networks";

/**
 * What a reseller may DO with an invoice XOP issued to it: download it and
 * share it. That is all — it is a bill to pay (2026-09-14). Editing,
 * duplicating, marking paid, deleting (singly or in bulk) and scheduling a
 * recurrence are XOP's side of the document, so the ported page renders none
 * of those controls, nor the row checkboxes that only ever fed bulk delete.
 */
export const RESELLER_MAY_MANAGE_INVOICES = false;

/**
 * The list's Customer column is dropped: every invoice here is addressed to
 * the RESELLER ITSELF, so a customer column would print the reseller's own
 * name on every row, or nothing (2026-09-14). The column config is the
 * command center's; the page filters it by key.
 */
export const RESELLER_INVOICES_HIDDEN_COLUMNS: readonly string[] = ["customer"];
